<template>
  <div v-if="isSvg(source)" class="inline-block">
    <NuxtImg
      :alt="alt || ''"
      :src="source"
      :provider="provider"
      :class="[
        noScale ? 'object-contain' : 'object-cover',
        isBrowserEdge ? 'pointer-events-none' : '',
        imageClass,
      ]"
      :loading="hasPriority ? 'eager' : 'lazy'"
      :fetchpriority="hasPriority ? 'high' : 'low'"
      @load="$emit('load')"
    />
  </div>
  <div
    v-else-if="source"
    class="size-full max-w-full overflow-hidden"
    :class="{ 'absolute inset-0': cover, relative: !cover }"
    :style="!cover ? `width: ${width ? `${width}px` : '100%'}` : ''"
  >
    <NuxtImg
      alt=""
      :src="source"
      :provider="provider"
      :sizes="defaultFallbackSizes"
      :modifiers="fallbackImageSettings.modifiers"
      :quality="fallbackImageSettings.modifiers.quality"
      class="relative inset-0 size-full opacity-0"
      :class="[
        noScale ? 'object-contain' : 'scale-125 object-cover object-top',
        isBrowserEdge ? 'pointer-events-none' : '',
        hidePreview ? 'opacity-0' : '',
        imageClass,
      ]"
      style="filter: blur(24px)"
      :loading="hasPriority ? 'eager' : 'lazy'"
      :fetchpriority="hasPriority ? 'high' : 'low'"
      :width="width"
      :height="height"
      aria-hidden="true"
    />
    <NuxtImg
      :alt="alt || ''"
      :src="source"
      :sizes="imageSizes"
      :provider="provider"
      :modifiers="mainImageModifiers"
      :quality="mainImageModifiers.quality"
      class="absolute inset-0 size-full"
      :class="[
        noScale ? 'object-contain' : 'object-cover object-top',
        isBrowserEdge ? 'pointer-events-none' : '',
        imageClass,
      ]"
      :width="width"
      :height="height"
      :loading="hasPriority ? 'eager' : 'lazy'"
      :fetchpriority="hasPriority ? 'high' : 'low'"
      @load="imageLoaded()"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue'
import type { Link } from '@unhead/vue'
import { useHead, useImage } from '#imports'
import useViewportPriority from '#layers/base/composables/useViewportPriority'
import {
  isSvg,
  fallbackImageSettings,
  defaultSizesOptimised,
  defaultFallbackSizes,
  notUpscaledDefaultSizes,
} from '~/composables/useSbImage'

type NuxtLazyImageProps = {
  source: string
  optimiseDesktop?: boolean
  sizes?: string
  quality?: number
  alt?: string
  imageClass?: string
  title?: string
  noScale?: boolean
  cover?: boolean
  lazy?: boolean
  highPriority?: boolean
  media?: 'all' | 'desktop' | 'mobile'
  provider?: string
}

const props = withDefaults(defineProps<NuxtLazyImageProps>(), {
  optimiseDesktop: false,
  sizes: undefined,
  quality: 70,
  alt: '',
  imageClass: undefined,
  title: undefined,
  noScale: false,
  cover: false,
  lazy: true,
  highPriority: false,
  media: 'all',
  provider: 'storyblok',
})

const emit = defineEmits<{ load: [] }>()

const { hasPriority } = useViewportPriority()
const img = useImage()
const provider = props.provider
const mainImageModifiers = {
  format: 'webp',
  quality: props.quality,
}

const hidePreview = ref(false)

const sizes = computed(() => {
  if (provider !== 'storyblok' || !props.source) {
    return null
  }
  return extractImageSizeFromUrl(props.source)
})

const width = computed(() => sizes.value?.width)
const height = computed(() => sizes.value?.height)

const imageSizes = computed(() => {
  if (props.sizes) {
    return props.sizes
  }
  if (props.optimiseDesktop) {
    return defaultSizesOptimised
  }
  return notUpscaledDefaultSizes(width.value ? width.value : 4000)
})

// Check if current browser is Microsoft Edge, if yes turn off visual search by setting pointer-events:none
const isBrowserEdge = computed(() =>
  import.meta.client ? navigator.userAgent.includes('Edg') : false,
)

const parseSize = (input: string | number | undefined | null = '') => {
  if (typeof input === 'number') {
    return input
  }
  if (typeof input === 'string' && input.replace('px', '').match(/^\d+$/g)) {
    return parseInt(input, 10)
  }
}

const preload = computed(() => {
  if (!props.source || isSvg(props.source)) {
    return []
  }
  const _sizes = img.getSizes(props.source, {
    sizes: imageSizes.value,
    provider,
    modifiers: {
      quality: mainImageModifiers.quality,
      width: parseSize(width.value),
      height: parseSize(height.value),
    },
  })
  let media: string
  if (!props.media || props.media === 'all') {
    media = 'all'
  } else if (props.media === 'desktop') {
    media = '(min-width: 1024px)'
  } else {
    media = '(max-width: 1023px)'
  }
  return [
    {
      // Include the media query in the dedup id so two instances of the same
      // asset with different `media` (e.g. desktop/mobile crops that fall back
      // to one CMS file) register distinct preloads instead of overwriting.
      hid: `${props.source}-${media}`,
      rel: 'preload',
      imagesrcset: _sizes.srcset,
      imagesizes: _sizes.sizes,
      as: 'image',
      media,
      ...(props.highPriority ? { priority: 'high' } : {}),
    },
  ]
})

useHead({ link: preload.value as Link[] })

const imageLoaded = () => {
  hidePreview.value = true
  emit('load')
}

function extractImageSizeFromUrl(url: string) {
  const match = url.match(/\/(\d+)x(\d+)\//)
  return match ? { width: Number(match[1]), height: Number(match[2]) } : null
}
</script>
